import type { ReactNode } from 'react';
import Header from '../components/Header';
import SemanticDOMBridge from '../components/SemanticDOMBridge';
import SiteFooter from '../components/SiteFooter';
import SkipLink from '../components/SkipLink';
import { CookieConsent } from '../components/CookieConsent';
import { useTheme } from '../contexts/ThemeContext';

export type LegalPageKind = 'privacy' | 'terms' | 'cookies';

const EFFECTIVE_DATE = 'August 3, 2026';

const pageLinks = [
  { label: 'Privacy Policy', href: '/privacy/' },
  { label: 'Terms of Service', href: '/terms/' },
  { label: 'Cookie Policy', href: '/cookies/' },
];

const sectionHeadingClass =
  'magazine-title scroll-mt-28 text-3xl tracking-[-0.025em] text-ink sm:text-4xl';
const bodyClass = 'text-base leading-7 text-muted';
const linkClass =
  'font-semibold text-ink underline decoration-line/40 underline-offset-4 transition-colors hover:text-accent';

interface LegalLayoutProps {
  kind: LegalPageKind;
  title: string;
  summary: string;
  sections: Array<{ id: string; label: string }>;
  children: ReactNode;
}

const LegalLayout = ({ kind, title, summary, sections, children }: LegalLayoutProps) => {
  const { theme, setTheme } = useTheme();
  const currentPath = `/${kind}/`;

  return (
    <div className="min-h-screen bg-canvas text-ink transition-colors duration-300">
      <SkipLink />
      <SemanticDOMBridge />
      <Header theme={theme} onThemeChange={setTheme} homeHref="/" sectionHrefPrefix="/" />

      <main id="main-content" className="pb-24 pt-28 sm:pb-32 sm:pt-36">
        <article
          className="mx-auto max-w-5xl px-6 lg:px-8"
          aria-labelledby={`${kind}-policy-title`}
          data-agent-id={`agent://consltr/legal/${kind}`}
          data-semantic-intent="read"
          data-policy-effective-date="2026-08-03"
          data-policy-owner="Consltr, LLC"
        >
          <header className="border-b border-line/10 pb-10 sm:pb-14">
            <p className="section-kicker">Legal / Consltr, LLC</p>
            <h1
              id={`${kind}-policy-title`}
              className="magazine-title mt-5 text-5xl tracking-[-0.045em] sm:text-7xl"
            >
              {title}
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-muted">{summary}</p>
            <p className="mt-5 font-mono text-xs uppercase tracking-[0.14em] text-faint">
              Effective and last updated: {EFFECTIVE_DATE}
            </p>
          </header>

          <div className="grid gap-12 pt-10 lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-16">
            <aside className="lg:sticky lg:top-28 lg:self-start" aria-label={`${title} contents`}>
              <nav aria-label={`${title} sections`}>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-faint">
                  On this page
                </p>
                <ol className="mt-4 space-y-3">
                  {sections.map((section, index) => (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        className="text-sm leading-5 text-muted transition-colors hover:text-ink"
                      >
                        <span className="mr-2 font-mono text-xs text-faint">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        {section.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>

              <nav aria-label="Other legal policies" className="mt-8 border-t border-line/10 pt-6">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-faint">
                  Related policies
                </p>
                <ul className="mt-4 space-y-2">
                  {pageLinks.map(link => (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        aria-current={link.href === currentPath ? 'page' : undefined}
                        className="text-sm text-muted underline decoration-line/30 underline-offset-4 hover:text-ink"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <div className="min-w-0 space-y-14">{children}</div>
          </div>
        </article>
      </main>

      <SiteFooter homeHref="/" sectionHrefPrefix="/" currentPath={currentPath} />
      <CookieConsent />
    </div>
  );
};

const LegalSection = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) => (
  <section id={id} aria-labelledby={`${id}-heading`} className="space-y-5">
    <h2 id={`${id}-heading`} className={sectionHeadingClass}>
      {title}
    </h2>
    {children}
  </section>
);

const PrivacyPolicy = () => {
  const sections = [
    { id: 'privacy-scope', label: 'Scope and controller' },
    { id: 'notice-at-collection', label: 'Notice at collection' },
    { id: 'information-collected', label: 'Information collected' },
    { id: 'uses-and-legal-bases', label: 'Uses and legal bases' },
    { id: 'analytics', label: 'Analytics and experiments' },
    { id: 'disclosures', label: 'How information is disclosed' },
    { id: 'retention', label: 'Retention and security' },
    { id: 'california-rights', label: 'California privacy rights' },
    { id: 'gdpr-rights', label: 'GDPR and international rights' },
    { id: 'children-and-changes', label: 'Children and policy changes' },
    { id: 'privacy-contact', label: 'Contact and requests' },
  ];

  return (
    <LegalLayout
      kind="privacy"
      title="Privacy Policy"
      summary="This policy explains what Consltr collects through consltr.com, why it is used, who receives it, and how people can exercise privacy rights."
      sections={sections}
    >
      <LegalSection id="privacy-scope" title="Scope and controller">
        <p className={bodyClass}>
          Consltr, LLC (“Consltr,” “we,” “us,” or “our”) operates{' '}
          <a href="https://consltr.com/" className={linkClass}>
            consltr.com
          </a>{' '}
          and is the controller or business responsible for the personal information described in
          this policy. Consltr is based in the San Francisco Bay Area, California. You can contact
          us at{' '}
          <a href="mailto:info@consltr.com" className={linkClass}>
            info@consltr.com
          </a>
          .
        </p>
        <p className={bodyClass}>
          This policy covers the public consulting website, its project-inquiry form, and related
          website analytics. It does not govern a client product shown in Selected Work, a linked
          third-party website, or data Consltr processes for a client under a separate agreement.
        </p>
      </LegalSection>

      <LegalSection id="notice-at-collection" title="Notice at collection">
        <div className="rounded-3xl border border-accent/25 bg-highlight/10 p-6 sm:p-7">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-accent">
            Before you submit a project inquiry
          </p>
          <p className={`${bodyClass} mt-4`}>
            Consltr collects identifiers and project-related professional or commercial information,
            including your name, email, company, project type, budget range, timeline, and message.
            We use it to review and respond to your request, plan a possible engagement, prevent
            abuse, and keep appropriate business records. Consltr does not sell this information or
            share it for cross-context behavioral advertising.
          </p>
        </div>
        <p className={bodyClass}>
          Providing a name, email, and project description is required to submit the form. Other
          fields are optional. Please do not include passwords, government identifiers, financial
          account details, health data, or other sensitive information in the free-text message.
        </p>
      </LegalSection>

      <LegalSection id="information-collected" title="Information we collect">
        <ul className="list-disc space-y-3 pl-6 text-base leading-7 text-muted">
          <li>
            <strong className="text-ink">Contact and identity information:</strong> name, email
            address, company, and information you include in correspondence.
          </li>
          <li>
            <strong className="text-ink">Project and commercial information:</strong> project type,
            description, budget range, timeline, source, and later business communications.
          </li>
          <li>
            <strong className="text-ink">Technical and usage information:</strong> page path, page
            title, referrer, device type, campaign parameters, interactions, timestamps, browser
            language, time zone, screen dimensions, session and visitor identifiers when permitted,
            and IP address processed by network services.
          </li>
          <li>
            <strong className="text-ink">Preference information:</strong> theme choice, consent
            choice, and analytics preference stored in your browser.
          </li>
          <li>
            <strong className="text-ink">Derived information:</strong> approximate region derived
            from IP address, experiment assignment, and aggregate engagement measurements.
          </li>
        </ul>
        <p className={bodyClass}>
          We collect this information directly from you, automatically from your browser and our
          infrastructure, and from providers that operate the website on our behalf.
        </p>
      </LegalSection>

      <LegalSection id="uses-and-legal-bases" title="How and why we use information">
        <dl className="grid gap-5 sm:grid-cols-2">
          {[
            [
              'Respond and take pre-contract steps',
              'Review inquiries, communicate with you, scope work, and prepare a possible proposal or agreement.',
            ],
            [
              'Operate and secure the site',
              'Deliver pages and APIs, prevent spam and misuse, troubleshoot failures, and protect Consltr and visitors.',
            ],
            [
              'Measure and improve',
              'Understand aggregate usage, evaluate accessible design and content, and measure A/B experiments through Salsa.',
            ],
            [
              'Meet legal obligations',
              'Maintain records, respond to lawful requests, establish or defend claims, and enforce these policies.',
            ],
          ].map(([term, description]) => (
            <div key={term} className="rounded-2xl border border-line/10 bg-panel p-5">
              <dt className="font-semibold text-ink">{term}</dt>
              <dd className="mt-2 text-sm leading-6 text-muted">{description}</dd>
            </div>
          ))}
        </dl>
        <p className={bodyClass}>
          Where the GDPR or similar law applies, our legal bases are steps requested before entering
          a contract, performance of a contract, legitimate interests in running and securing our
          business, compliance with law, and consent for non-essential browser storage or analytics
          where consent is required. You may withdraw consent at any time without affecting earlier
          lawful processing.
        </p>
      </LegalSection>

      <LegalSection id="analytics" title="Salsa analytics and A/B experiments">
        <p className={bodyClass}>
          The site uses the first-party Salsa Analytics SDK at cloud.salsas.io. Before you make a
          choice, Salsa operates in cookieless mode: it sends aggregate page-view, click, scroll,
          form-submit, and experiment events with an in-memory page session and no persistent
          visitor identifier. These events can include the page path and title, referrer, device
          class, campaign parameters, interaction labels, and a request IP used server-side to
          derive an approximate region.
        </p>
        <p className={bodyClass}>
          If you allow analytics, Salsa also uses a random first-party visitor identifier, a session
          identifier, browser language, time zone, and screen dimensions so visits and experiment
          assignments can remain consistent. Choosing “Required only” stops Salsa analytics and
          removes the Salsa visitor and session identifiers from browser storage. Consltr does not
          intentionally send the inquiry form’s name, email, company, or free-text message to Salsa.
          Salsa does not use third-party cookies, cross-site tracking, or fingerprinting on this
          site.
        </p>
        <p className={bodyClass}>
          See the{' '}
          <a href="/cookies/" className={linkClass}>
            Cookie Policy
          </a>{' '}
          for the exact browser-storage inventory and how to change your choice.
        </p>
      </LegalSection>

      <LegalSection id="disclosures" title="How information is disclosed">
        <p className={bodyClass}>
          Consltr may disclose information only as reasonably necessary to the following recipients:
        </p>
        <ul className="list-disc space-y-3 pl-6 text-base leading-7 text-muted">
          <li>
            <strong className="text-ink">Service providers:</strong> Salsa for analytics and
            experimentation; DigitalOcean and technical infrastructure providers for hosting;
            database, backup, security, and email-delivery providers; and Google Fonts when the
            browser requests font files.
          </li>
          <li>
            <strong className="text-ink">Professional advisers:</strong> lawyers, accountants,
            insurers, and other advisers subject to appropriate duties.
          </li>
          <li>
            <strong className="text-ink">Legal and safety recipients:</strong> authorities or other
            parties when required by law or reasonably necessary to protect rights, safety, and
            system integrity.
          </li>
          <li>
            <strong className="text-ink">Business transaction recipients:</strong> a successor or
            counterparty in a merger, financing, reorganization, or sale, subject to appropriate
            protections.
          </li>
        </ul>
        <p className={bodyClass}>
          Consltr does not sell personal information, share it for cross-context behavioral
          advertising, or offer financial incentives for personal information. We do not use
          sensitive personal information to infer characteristics.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="Retention and security">
        <p className={bodyClass}>
          Consltr keeps personal information only as long as reasonably necessary for the purposes
          described above. The period depends on whether an inquiry remains active, becomes a client
          engagement, is needed for legitimate business records, or must be retained for tax, legal,
          security, dispute, or enforcement reasons. We delete or de-identify information when it is
          no longer needed. Browser-storage periods are listed in the Cookie Policy.
        </p>
        <p className={bodyClass}>
          We use administrative, technical, and physical safeguards appropriate to the nature of the
          information, including encrypted transport, access controls, rate limits, hardened
          production services, and restricted database access. No system is completely secure, so we
          cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection id="california-rights" title="California privacy rights">
        <p className={bodyClass}>
          Where the California Consumer Privacy Act and California Privacy Rights Act apply,
          California residents may request access to categories and specific pieces of personal
          information, correction, deletion, information about collection and disclosure, and
          portability. They may also opt out of sale or sharing and limit certain uses of sensitive
          personal information, and they have a right not to receive discriminatory treatment for
          exercising privacy rights.
        </p>
        <p className={bodyClass}>
          During the preceding 12 months, Consltr may have collected the categories described above:
          identifiers; professional, commercial, and project information; internet or electronic
          network activity; approximate geolocation derived from IP; and limited inferences such as
          experiment assignments. These categories are disclosed to service providers for the
          business purposes described in this policy. They are not sold or shared for cross-context
          behavioral advertising, including for people under 16.
        </p>
        <p className={bodyClass}>
          To submit a request, email{' '}
          <a
            href="mailto:info@consltr.com?subject=California%20Privacy%20Request"
            className={linkClass}
          >
            info@consltr.com
          </a>{' '}
          with the subject “California Privacy Request.” We will verify the request using
          information reasonably related to your relationship with Consltr. An authorized agent may
          submit a request with proof of authority. We honor legally recognized opt-out preference
          signals, including Global Privacy Control, where applicable. Because Consltr does not sell
          or share personal information, a separate “Do Not Sell or Share” link is not currently
          required for our practices.
        </p>
        <p className={bodyClass}>
          California’s official explanation of these rights is available from the{' '}
          <a
            href="https://oag.ca.gov/privacy/ccpa"
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            California Attorney General
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection id="gdpr-rights" title="GDPR and international privacy rights">
        <p className={bodyClass}>
          If the GDPR, UK GDPR, or a similar law applies to you, you may have rights to be informed,
          access personal data, correct inaccurate data, erase data, restrict processing, receive
          portable data, object to processing based on legitimate interests, and withdraw consent.
          You may also complain to the data-protection authority where you live or work. Consltr
          does not use website information for solely automated decisions that produce legal or
          similarly significant effects; A/B assignments only change website presentation.
        </p>
        <p className={bodyClass}>
          Consltr is established in the United States, and the providers described above may process
          information in the United States or other countries. Where required, transfers are made
          under an approved legal mechanism and appropriate safeguards. Contact us for information
          relevant to a particular transfer or request.
        </p>
        <p className={bodyClass}>
          The European Commission provides an official overview of{' '}
          <a
            href="https://commission.europa.eu/law/law-topic/data-protection/information-individuals_en"
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            individual data-protection rights
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection id="children-and-changes" title="Children and policy changes">
        <p className={bodyClass}>
          This business-to-business consulting site is not directed to children under 16, and
          Consltr does not knowingly collect personal information from them. If you believe a child
          submitted information, contact us so we can review and delete it as appropriate.
        </p>
        <p className={bodyClass}>
          We may update this policy when our practices, vendors, or legal obligations change. The
          “last updated” date will change, and material changes will receive additional notice where
          required.
        </p>
      </LegalSection>

      <LegalSection id="privacy-contact" title="Contact and privacy requests">
        <p className={bodyClass}>
          Email privacy questions or requests to{' '}
          <a href="mailto:info@consltr.com?subject=Privacy%20Request" className={linkClass}>
            info@consltr.com
          </a>
          . Include “Privacy Request” in the subject and identify the right you want to exercise. Do
          not send identity documents unless we specifically request a secure verification method.
        </p>
        <address className="not-italic text-base leading-7 text-muted">
          Consltr, LLC
          <br />
          San Francisco Bay Area, California, United States
          <br />
          <a href="mailto:info@consltr.com" className={linkClass}>
            info@consltr.com
          </a>
        </address>
      </LegalSection>
    </LegalLayout>
  );
};

const TermsOfService = () => {
  const sections = [
    { id: 'terms-acceptance', label: 'Acceptance and eligibility' },
    { id: 'terms-site-purpose', label: 'Site purpose and engagements' },
    { id: 'terms-permitted-use', label: 'Permitted use' },
    { id: 'terms-intellectual-property', label: 'Intellectual property' },
    { id: 'terms-inquiries', label: 'Inquiries and confidentiality' },
    { id: 'terms-third-parties', label: 'Third-party services' },
    { id: 'terms-disclaimers', label: 'Disclaimers' },
    { id: 'terms-liability', label: 'Limitation of liability' },
    { id: 'terms-governing-law', label: 'Governing law and disputes' },
    { id: 'terms-changes-contact', label: 'Changes and contact' },
  ];

  return (
    <LegalLayout
      kind="terms"
      title="Terms of Service"
      summary="These terms govern access to the public Consltr website. Client consulting work is governed by a separate written agreement, not by these website terms."
      sections={sections}
    >
      <LegalSection id="terms-acceptance" title="Acceptance and eligibility">
        <p className={bodyClass}>
          By accessing or using consltr.com, you agree to these Terms of Service and the linked
          Privacy and Cookie Policies. If you do not agree, do not use the site. You must be able to
          form a binding contract where you live. If you use the site for an organization, you
          represent that you have authority to act for it.
        </p>
      </LegalSection>

      <LegalSection id="terms-site-purpose" title="Site purpose and consulting engagements">
        <p className={bodyClass}>
          The site describes Consltr’s capabilities, selected work, and ways to start a
          conversation. Website content and a submitted inquiry are informational only; neither
          creates an attorney-client, fiduciary, employment, partnership, agency, or consulting
          relationship. A consulting engagement begins only when Consltr and the client sign a
          separate agreement, statement of work, or other written contract. That agreement controls
          if it conflicts with these website terms.
        </p>
        <p className={bodyClass}>
          Site content is not legal, tax, accounting, investment, medical, or other regulated
          professional advice. You are responsible for obtaining advice appropriate to your
          situation.
        </p>
      </LegalSection>

      <LegalSection id="terms-permitted-use" title="Permitted and prohibited use">
        <p className={bodyClass}>
          You may browse the site, share links, and submit a truthful project inquiry for legitimate
          business purposes. Automated agents may read public machine-discovery files and submit the
          documented inquiry API only when acting with authorization, using truthful information,
          and complying with rate limits.
        </p>
        <p className={bodyClass}>You may not:</p>
        <ul className="list-disc space-y-3 pl-6 text-base leading-7 text-muted">
          <li>send spam, deceptive inquiries, malware, or unlawful material;</li>
          <li>probe, scan, disrupt, overload, or bypass security or access controls;</li>
          <li>scrape or use the site in a way that violates law or another person’s rights;</li>
          <li>
            misrepresent affiliation with Consltr or use Consltr branding to imply endorsement;
          </li>
          <li>reverse engineer non-public systems except where law expressly permits it; or</li>
          <li>interfere with other people’s use of the site.</li>
        </ul>
      </LegalSection>

      <LegalSection id="terms-intellectual-property" title="Intellectual property">
        <p className={bodyClass}>
          The site’s original design, code, text, graphics, trademarks, and other content are owned
          by Consltr or used with permission and are protected by applicable intellectual-property
          laws. Limited personal or internal-business viewing does not transfer ownership. Client
          names, product names, logos, screenshots, and linked materials remain the property of
          their respective owners.
        </p>
        <p className={bodyClass}>
          If you send feedback about the public site, you permit Consltr to use it without
          restriction or compensation, provided we do not publicly identify you without permission.
        </p>
      </LegalSection>

      <LegalSection id="terms-inquiries" title="Project inquiries and confidentiality">
        <p className={bodyClass}>
          Do not submit trade secrets, source credentials, regulated personal data, or confidential
          information through the public inquiry form. Sending an inquiry does not create a
          confidentiality obligation or non-disclosure agreement. If confidential evaluation is
          necessary, first ask Consltr to establish an appropriate written process.
        </p>
        <p className={bodyClass}>
          You represent that you have the right to send the information in your inquiry. Consltr may
          decline or stop responding to any inquiry at its discretion, subject to applicable law.
        </p>
      </LegalSection>

      <LegalSection id="terms-third-parties" title="Third-party websites and services">
        <p className={bodyClass}>
          Selected Work and social links lead to websites operated by clients or third parties.
          Consltr does not control their availability, content, security, accessibility, or privacy
          practices. A link is provided for context and does not guarantee or endorse every
          statement, product, or practice on the destination site.
        </p>
      </LegalSection>

      <LegalSection id="terms-disclaimers" title="Disclaimers">
        <p className={bodyClass}>
          To the fullest extent permitted by law, the public site and its content are provided “as
          is” and “as available.” Consltr disclaims implied warranties of merchantability, fitness
          for a particular purpose, title, and non-infringement. We do not promise that the site
          will be uninterrupted, error-free, secure, or current, or that portfolio outcomes can be
          reproduced for another project.
        </p>
        <p className={bodyClass}>
          Nothing in these terms excludes a warranty or consumer protection that cannot legally be
          excluded.
        </p>
      </LegalSection>

      <LegalSection id="terms-liability" title="Limitation of liability">
        <p className={bodyClass}>
          To the fullest extent permitted by law, Consltr and its members, personnel, and service
          providers will not be liable for indirect, incidental, special, consequential, exemplary,
          or punitive damages, loss of profits, data, goodwill, or business opportunity arising from
          use of the public site. Consltr’s total liability relating to the public site will not
          exceed the greater of US $100 or the amount you paid Consltr specifically for access to
          the public site during the 12 months before the claim.
        </p>
        <p className={bodyClass}>
          These limits do not apply where prohibited, or to liability that cannot be limited under
          applicable law. Liability under a consulting engagement is governed by its separate
          agreement.
        </p>
      </LegalSection>

      <LegalSection id="terms-governing-law" title="Governing law and disputes">
        <p className={bodyClass}>
          These terms are governed by California law, without regard to conflict-of-law rules.
          Unless applicable consumer law requires otherwise, disputes relating only to the public
          site will be brought in the state or federal courts located in San Francisco County,
          California, and each party consents to their jurisdiction. Before filing, the parties
          should attempt in good faith to resolve the issue by written notice to the contact below.
        </p>
        <p className={bodyClass}>
          If any provision is unenforceable, it will be limited to the minimum extent necessary and
          the remaining provisions will continue. Failure to enforce a provision is not a waiver.
        </p>
      </LegalSection>

      <LegalSection id="terms-changes-contact" title="Changes, accessibility, and contact">
        <p className={bodyClass}>
          We may update these terms by posting a revised version and changing the last-updated date.
          Continued use after an update means the revised terms apply from their effective date.
        </p>
        <p className={bodyClass}>
          For terms questions, copyright concerns, or help accessing site content in another format,
          email{' '}
          <a href="mailto:info@consltr.com" className={linkClass}>
            info@consltr.com
          </a>
          .
        </p>
        <address className="not-italic text-base leading-7 text-muted">
          Consltr, LLC
          <br />
          San Francisco Bay Area, California, United States
        </address>
      </LegalSection>
    </LegalLayout>
  );
};

const CookiePolicy = () => {
  const sections = [
    { id: 'cookie-overview', label: 'Overview' },
    { id: 'cookie-inventory', label: 'Storage inventory' },
    { id: 'cookie-salsa', label: 'Salsa analytics behavior' },
    { id: 'cookie-choices', label: 'Your choices' },
    { id: 'cookie-third-parties', label: 'Third-party requests' },
    { id: 'cookie-changes-contact', label: 'Changes and contact' },
  ];

  const storageRows = [
    {
      name: 'salsa_cookie_consent',
      location: 'Local storage',
      purpose: 'Records whether analytics is allowed. Required to remember your privacy choice.',
      duration: 'Until you change the choice or clear site data.',
      category: 'Essential',
    },
    {
      name: 'cookieConsent and cookieSettings',
      location: 'Local storage',
      purpose:
        'Compatibility records for visitors who used an earlier version of the consent control.',
      duration: 'Until cleared or replaced.',
      category: 'Essential',
    },
    {
      name: 'theme',
      location: 'Local storage',
      purpose: 'Remembers a visitor-requested light or dark appearance.',
      duration: 'Until changed or cleared.',
      category: 'Preference',
    },
    {
      name: 'sa_visitor',
      location: 'Local storage',
      purpose:
        'Random first-party Salsa visitor identifier for repeat-visit measurement and stable A/B assignment.',
      duration: 'Only with analytics consent; until consent is withdrawn or site data is cleared.',
      category: 'Analytics',
    },
    {
      name: 'sa_session',
      location: 'Session storage',
      purpose:
        'Salsa session identifier. The SDK starts a new logical session after 30 minutes of inactivity.',
      duration:
        'Only with analytics consent; removed when consent is withdrawn or the browser session ends.',
      category: 'Analytics',
    },
  ];

  return (
    <LegalLayout
      kind="cookies"
      title="Cookie Policy"
      summary="Consltr’s public site currently uses browser storage rather than third-party advertising cookies. This page lists every site-managed key and explains Salsa’s cookieless and consented modes."
      sections={sections}
    >
      <LegalSection id="cookie-overview" title="Cookies and similar technologies">
        <p className={bodyClass}>
          Cookies are small files stored by a website. Local storage and session storage are similar
          browser technologies that save information on a device. This policy uses “browser storage”
          to cover all of them.
        </p>
        <p className={bodyClass}>
          The public Consltr consulting site does not currently use third-party advertising cookies.
          It uses local storage for theme and consent preferences and, only when analytics is
          allowed, first-party Salsa visitor and session identifiers. Essential and requested
          preference storage supports the service you asked for; non-essential analytics identifiers
          remain off unless you allow them.
        </p>
      </LegalSection>

      <LegalSection id="cookie-inventory" title="Current browser-storage inventory">
        <div
          className="overflow-x-auto rounded-2xl border border-line/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          role="region"
          aria-label="Complete browser-storage inventory; scroll horizontally to view every column"
          tabIndex={0}
        >
          <table className="w-full min-w-[46rem] border-collapse text-left text-sm">
            <caption className="sr-only">
              Browser storage used by consltr.com, including its category, location, purpose, and
              duration
            </caption>
            <thead className="bg-panel-alt text-ink">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Name
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Category
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Location
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Purpose
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody>
              {storageRows.map(row => (
                <tr key={row.name} className="border-t border-line/10 align-top text-muted">
                  <th scope="row" className="px-4 py-4 font-mono text-xs font-semibold text-ink">
                    {row.name}
                  </th>
                  <td className="px-4 py-4">{row.category}</td>
                  <td className="px-4 py-4">{row.location}</td>
                  <td className="px-4 py-4 leading-6">{row.purpose}</td>
                  <td className="px-4 py-4 leading-6">{row.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LegalSection>

      <LegalSection id="cookie-salsa" title="How Salsa analytics behaves">
        <p className={bodyClass}>
          Salsa begins in cookieless mode before you choose. In that mode, it sends aggregate events
          using an in-memory page-session identifier; it does not set <code>sa_visitor</code> or{' '}
          <code>sa_session</code>. If you choose “Required only,” Salsa analytics stops.
        </p>
        <p className={bodyClass}>
          If you choose “Allow analytics,” Salsa creates <code>sa_visitor</code> and{' '}
          <code>sa_session</code> and measures page views, click labels, scroll depth, form-submit
          labels, experiment exposures, and conversions. It also receives page and device context as
          described in the Privacy Policy. It does not receive inquiry form field values through its
          automatic form event, and it does not use cross-site tracking or browser fingerprinting.
        </p>
      </LegalSection>

      <LegalSection id="cookie-choices" title="Change or withdraw your choice">
        <p className={bodyClass}>
          Use the “Cookie settings” button in the footer on any page. Withdrawing analytics consent
          sends an update to Salsa and removes <code>sa_visitor</code> and <code>sa_session</code>{' '}
          from browser storage. Withdrawing consent is as available as granting it, and the public
          site remains usable with required storage only.
        </p>
        <p className={bodyClass}>
          You can also inspect, block, or delete site data in browser settings. Clearing all site
          data removes your saved choice, so the consent control will appear again. Global Privacy
          Control is honored where legally applicable; Consltr does not sell or share personal
          information for cross-context behavioral advertising.
        </p>
      </LegalSection>

      <LegalSection id="cookie-third-parties" title="Third-party network requests">
        <p className={bodyClass}>
          The page loads the Salsa SDK from cloud.salsas.io and font resources from Google Fonts.
          These providers receive ordinary network information needed to deliver files, such as IP
          address, request time, and browser headers. Consltr does not enable Google Analytics or
          third-party advertising technology on the public consulting site.
        </p>
        <p className={bodyClass}>
          The European Union provides official guidance on{' '}
          <a
            href="https://europa.eu/youreurope/business/growing/digitalising/online-privacy/index_en.htm"
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            cookie consent and withdrawal
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection id="cookie-changes-contact" title="Changes and contact">
        <p className={bodyClass}>
          We will update this inventory before materially changing the storage or tracking
          technologies used by the public site. Questions can be sent to{' '}
          <a href="mailto:info@consltr.com?subject=Cookie%20Policy" className={linkClass}>
            info@consltr.com
          </a>
          . For broader information about personal-data handling, read the{' '}
          <a href="/privacy/" className={linkClass}>
            Privacy Policy
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
};

export const LegalPage = ({ kind }: { kind: LegalPageKind }) => {
  if (kind === 'privacy') return <PrivacyPolicy />;
  if (kind === 'terms') return <TermsOfService />;
  return <CookiePolicy />;
};
